import { Component, OnInit, Input, Output, EventEmitter, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { F4626Service } from '../../services/f4626.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
import { safeEvalFormula } from '../../../shared/_utils/safe-eval-formula';

declare const _: any;

@Component({
    selector: 'gtw-f4626-ce',
    templateUrl: './f4626.component.html',
    styleUrls: ['./f4626.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class F4626Component implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // ✅ CRITICAL: F7004 SUCCESS PATTERN - @Input properties with setters
  @Input('filter-params')
  set _filter_params(filterParams: string) {
    try {
      let params = safeJsonInput<any>(filterParams, null, 'f4626.parse1');
      if (Object.keys(params).length > 0) {
        this.filterParams = params;
        this.tryInit();
      }
    } catch (e) {
      console.log("Filter params input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('header-data')
  set _header_data(headerData: string) {
    try {
      let data = safeJsonInput<any>(headerData, null, 'f4626.parse2');
      if (Object.keys(data).length > 0) {
        this.headerData = data;
        this.globalService = data.globalParams;
        this.USER_SETTINGS = data.userSettingsData;
        this.SERVER_CONFIG = data.serverConfigData;
        this.GENERAL_CONFIG = data.generalConfigData;
        this.tryInit();
      }
    } catch (e) {
      console.log("Header data input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('row-data')
  set _row_data(rowData: string) {
    try {
      let data = safeJsonInput<any>(rowData, null, 'f4626.parse3');
      if (Object.keys(data).length > 0) {
        this.rowData = data;
        this.tryInit();
      }
    } catch (e) {
      console.log("Row data input not yet stable. Still waiting to get resolved.");
    }
  }

  @Input('base-urls')
  set _base_urls(baseUrls: string) {
    try {
      let urls = safeJsonInput<any>(baseUrls, null, 'f4626.parse4');
      if (Object.keys(urls).length > 0) {
        this.baseUrls = urls;
        this.tryInit();
      }
    } catch (e) {
      console.log("Base URLs input not yet stable. Still waiting to get resolved.");
    }
  }

  private isInitialized = false;

  // Component properties
  rowData: any = {};
  headerData: any = {};
  filterParams: any = {};
  baseUrls: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  // STANDARD COMPONENT PROPERTIES (MANDATORY from superprompt)
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  loading = false;

  // Entity data properties
  entityList: { key: string; value: any }[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';

  // F4626 specific properties
  f4626Header: any = {};
  f4626List: any[] = [];
  f4626OriginalData: any[] = [];
  f4626FormData: any[] = [];
  f4626ColumnHeader: any[] = [];
  f4626FinalData: any[] = [];
  f4626LHData: any[] = [];
  f4626CorpDetails: any[] = [];
  CountriesList: any = {};
  allCountries: any[] = [];
  unAllocatedAmt: number = 0;
  selectedTabName: string = "Part I";
  currentPart: string = "P1";
  currentSchedule: string = "N/A";
  startDate: Date = new Date("01/01/2019");
  endDate: Date = new Date();
  convensions: string[] = ['HY', 'MM', 'MQ', 'S/L'];
  methods: string[] = ['150 DB', '200 DB', 'DB', 'S/L'];

  // Tab configuration - matching original AngularJS
  activeTab = 1;
  tabs = [
    { id: 1, name: 'Part I' },
    { id: 2, name: 'Part II' },
    { id: 3, name: 'Part III' },
    { id: 4, name: 'Part IV - Section 1' },
    { id: 5, name: 'Part IV - Section 3' },
    { id: 6, name: 'Part VI - Section 2' }
  ];

  modalTitle: string = '';

  constructor(
    private f4626Service: F4626Service,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService
  ) { }

  ngOnInit(): void {
    // Component initialization happens in init() which is gated by tryInit()
    // and triggered once all required @Input setters have resolved.
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }
    if (!this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }
    if (!this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
      return;
    }
    if (!this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.setupEntityData();  // ALWAYS call this first
        this.endDate = new Date("12/31/" + (this.filterParams?.tax_year || new Date().getFullYear()));
        this.getF4626Data();
      }
    } catch (e) {
      console.error('Error in F4626 init:', e);
    }
  }

  private setupEntityData(): void {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    // 🔴 MANDATORY ORDER: Tax Year, LEID, LE Name
    this.entityList = [
      { key: "Tax Year", value: this.rowData.TAX_YEAR },
      { key: "LEID", value: this.rowData.LEID },
      { key: "LE Name", value: this.rowData.LE_NAME }
    ];
  }

  onTabChange(tabIndex: number): void {
    this.activeTab = tabIndex + 1;
    const tab = this.tabs[tabIndex];
    
    if (tab) {
      this.selectedTabName = tab.name;
      
      switch (tab.name) {
        case "Part I":
          this.currentPart = "P1";
          this.currentSchedule = "N/A";
          break;
        case "Part II":
          this.currentPart = "P2";
          this.currentSchedule = "N/A";
          break;
        case "Part III":
          this.currentPart = "P3";
          this.currentSchedule = "N/A";
          break;
        case "Part IV - Section 1":
          this.currentPart = "P4";
          this.currentSchedule = "1";
          break;
        case "Part IV - Section 3":
          this.currentPart = "P4";
          this.currentSchedule = "3";
          break;
        case "Part VI - Section 2":
          this.currentPart = "P6";
          this.currentSchedule = "II";
          break;
        default:
          this.currentPart = "P1";
          this.currentSchedule = "N/A";
          break;
      }

      // Reset data for new tab
      this.f4626List = [];
      this.f4626OriginalData = [];
      this.f4626FormData = [];
      this.f4626ColumnHeader = [];
      this.f4626FinalData = [];
      this.f4626LHData = [];
      this.CountriesList = {};
      this.unAllocatedAmt = 0;

      if (this.currentSchedule === "B") {
        this.fetchCountryList();
      }

      this.getF4626Data();
    }
  }

  getF4626Data(): void {
    this.loading = true;
    this.f4626Service.fetch4626Details(
      this.GENERAL_CONFIG.base_url,
      this.rowData,
      this.currentPart,
      this.currentSchedule,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      data => {
        if (data.callSuccess && data.callSuccess.length > 0 && data.callSuccess !== '1') {
          this.gtwSnackbar.openSnackBar(`Error loading data: ${data.errorMessage}`, 'gtw-snackbar--red', true, 4);
        } else {
          if (data.jsonObject && data.jsonObject.length > 0) {
            this.processF4626Data(data.jsonObject);
          } else {
            this.gtwSnackbar.openSnackBar('No data found!', 'gtw-snackbar--yellow', true, 4);
          }
        }
        this.loading = false;
      },
      error => {
        console.error('Error fetching F4626 data:', error);
        this.gtwSnackbar.openSnackBar('Failed to load data.', 'gtw-snackbar--red', true, 4);
        this.loading = false;
      }
    );
  }

  private processF4626Data(data: any[]): void {
    // Extract header if exists
    if (data[0]?.ROW_TYPE === 'FH') {
      this.f4626Header = data[0];
      this.modalTitle = this.f4626Header?.DESCRIPTION || this.f4626Header.ATTRIB_DESCRIPTION || 'Form 4626';
      this.f4626List = data.slice(1);
    } else {
      this.f4626List = data;
    }

    // Group by LINE_ORDER - matching original AngularJS logic
    const groupedByLineOrder = this.f4626List.reduce((acc, item) => {
      acc[item.LINE_ORDER] = acc[item.LINE_ORDER] || [];
      acc[item.LINE_ORDER].push(item);
      return acc;
    }, {});

    this.f4626FormData = Object.keys(groupedByLineOrder).map(k => ({
      ROW_TYPE: groupedByLineOrder[k][0].ROW_TYPE,
      DELETE_ROWS: [],
      Line_data: groupedByLineOrder[k]
    }));

    // Process LH data (complex nested structure) - matching original logic
    this.processLHData();

    // Process form data types - matching original logic
    this.processFormDataTypes();

    // Extract corporation details if exists
    if (this.f4626FormData[0]?.Line_data && this.f4626FormData[0].Line_data[0]?.ROW_TYPE === 'PH') {
      this.f4626CorpDetails = this.f4626FormData[0];
      this.f4626FormData.splice(0, 1);
    }

    // Save original data for comparison
    this.f4626OriginalData = _.cloneDeep(this.f4626FormData);
  }

  private processLHData(): void {
    // Complex LH processing logic - matching original AngularJS
    const LH_index = this.f4626FormData.findIndex(x => x.ROW_TYPE === "LH");
    
    if (LH_index === -1) return;

    // Group by ROW_TYPE
    const groupedByRowType = this.f4626FormData.reduce((acc, item) => {
      acc[item.ROW_TYPE] = acc[item.ROW_TYPE] || [];
      acc[item.ROW_TYPE].push(item);
      return acc;
    }, {});

    // Process LH specific logic
    const lhData = Object.keys(groupedByRowType)
      .map(k => {
        if (k === 'LH') {
          return { ROW_TYPE: groupedByRowType[k][0].ROW_TYPE, DELETE_ROWS: [], Line_data: groupedByRowType[k] };
        }
        return null;
      })
      .filter(item => item !== null);

    // Remove LH items from form data
    this.f4626FormData = this.f4626FormData.filter(item => item.ROW_TYPE !== 'LH');

    // Process occurrence data within LH
    if (lhData.length > 0) {
      for (const lhItem of lhData) {
        if (!lhItem) continue;
        const lineData = lhItem.Line_data;
        if (lineData && lineData.length > 0) {
          for (let h = 0; h < lineData.length; h++) {
            const currentLineData = lineData[h];
            if (currentLineData && currentLineData.Line_data && currentLineData.Line_data[0]?.OCCURRENCE != null) {
              const groupedByOccurrence = currentLineData.Line_data.reduce((acc: any, item: any) => {
                acc[item.OCCURRENCE] = acc[item.OCCURRENCE] || [];
                acc[item.OCCURRENCE].push(item);
                return acc;
              }, {});

              const occurrenceData = Object.keys(groupedByOccurrence).map(k => ({
                ROW_TYPE: groupedByOccurrence[k][0].ROW_TYPE,
                DELETE_ROWS: [],
                Line_data: groupedByOccurrence[k]
              }));

              // Replace with first occurrence and insert others
              lineData.splice(h, 1, occurrenceData[0]);
              if (occurrenceData.length > 1) {
                for (let a = 1; a < occurrenceData.length; a++) {
                  lineData.splice(h + a, 0, occurrenceData[a]);
                }
              }
            }
          }
        }
        this.f4626LHData = [lhItem];
      }

      // Insert LH data back at original position
      this.f4626FormData.splice(LH_index, 0, this.f4626LHData);
    }
  }

  private processFormDataTypes(): void {
    for (let i = 0; i < this.f4626FormData.length; i++) {
      if (this.f4626FormData[i].ROW_TYPE === 'DT' || this.f4626FormData[i].ROW_TYPE === 'TH') {
        for (let j = 0; j < this.f4626FormData[i].Line_data.length; j++) {
          const item = this.f4626FormData[i].Line_data[j];
          
          // Process date fields
          if (item.DATA_TYPE === "dateVarious" && item.EDIT_TYPE === "custom_date_various") {
            item.ATTRIB_VALUE = new Date(item.ATTRIB_VALUE);
          }
          
          // Process label fields
          if (item.EDIT_TYPE === 'label' && item.DATA_TYPE === 'string' && item.IS_EDITABLE === 'Y') {
            item.ATTRIB_VALUE = item.ATTRIB_DESCRIPTION;
          }
          
          // Process currency fields
          if (item.EDIT_TYPE === 'currency' && item.DATA_TYPE === 'number' && item.IS_EDITABLE === 'Y') {
            item.ATTRIB_VALUE = parseInt(item.ATTRIB_VALUE) || 0;
          }
        }
      } else if (this.f4626FormData[i].ROW_TYPE === 'LH') {
        this.processLHFormDataTypes(this.f4626FormData[i]);
      }
    }
  }

  private processLHFormDataTypes(lhData: any): void {
    for (let j = 0; j < lhData.Line_data.length; j++) {
      for (let l = 0; l < lhData.Line_data[j].Line_data.length; l++) {
        const item = lhData.Line_data[j].Line_data[l];
        
        if (item.IS_EDITABLE === 'Y') {
          // Process label/text fields
          if ((item.EDIT_TYPE === 'label' || item.EDIT_TYPE === 'text') && 
              item.DATA_TYPE === 'string' && item.ATTRIB_VALUE === undefined) {
            item.ATTRIB_VALUE = item.ATTRIB_DESCRIPTION;
          }
          
          // Process currency fields
          if (item.EDIT_TYPE === 'currency' && item.DATA_TYPE === 'number') {
            item.ATTRIB_VALUE = parseInt(item.ATTRIB_VALUE) || 0;
          }
          
          // Process date fields
          if (item.DATA_TYPE === "dateVarious" && item.EDIT_TYPE === "custom_date_various") {
            item.ATTRIB_VALUE = new Date(item.ATTRIB_VALUE);
          }
        }
      }
    }
  }

  fetchCountryList(): void {
    this.f4626Service.loadCountryList(this.GENERAL_CONFIG.base_url).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      data => {
        this.allCountries = [];
        this.CountriesList = {};
        if (data.jsonObject) {
          data.jsonObject.forEach((country: any) => {
            this.allCountries.push({ LABEL: country.COUNTRY_DESC, VALUE: country.COUNTRY_CODE });
          });
          this.CountriesList = {
            param_name: "Countries",
            values: this.allCountries,
            label_name: "label",
            value_name: "value",
            label: "Countries",
            selected: []
          };
        }
      },
      error => {
        console.error('Error loading country list:', error);
        this.gtwSnackbar.openSnackBar('Failed to load country list.', 'gtw-snackbar--red', true, 4);
      }
    );
  }

  valuechanged(parent_index: number, occurrence: any, line_no: string, index: number): void {
    // Matching original AngularJS logic
    for (let i = 0; i < this.f4626FormData.length; i++) {
      if (this.f4626FormData[i].ROW_TYPE === 'DT' || 
          this.f4626FormData[i].ROW_TYPE === 'RT' || 
          this.f4626FormData[i].ROW_TYPE === 'TH' || 
          this.f4626FormData[i].ROW_TYPE === 'MD') {
        if (i === parent_index && this.f4626FormData[i].Line_data[index]) {
          this.f4626FormData[i].Line_data[index].IS_CHANGED_FLAG = "Y";
          console.log(`F4626 Setting IS_CHANGED_FLAG=Y for item:`, this.f4626FormData[i].Line_data[index]);
        }
      } else if (this.f4626FormData[i].ROW_TYPE === 'LH') {
        if (this.f4626FormData[i].Line_data[parent_index]?.Line_data[index]?.LINE_NO === line_no) {
          this.f4626FormData[i].Line_data[parent_index].Line_data[index].IS_CHANGED_FLAG = "Y";
          console.log(`F4626 Setting IS_CHANGED_FLAG=Y for LH item:`, this.f4626FormData[i].Line_data[parent_index].Line_data[index]);
        }
      }
    }
    
    // Trigger calculations when any value changes
    this.calcFinalAmounts();
  }

  updateAmount(index: number, line_no: string): void {
    // Trigger calculation of dependent fields when amount changes
    this.calcFinalAmounts();
  }

  calcFinalAmounts(): void {
    // Original calculation logic preserved
    for (let i = 0; i < this.f4626FormData.length; i++) {
      if (this.f4626FormData[i].ROW_TYPE === 'LH') {
        for (let j = 0; j < this.f4626FormData[i].Line_data.length; j++) {
          for (let k = 0; k < this.f4626FormData[i].Line_data[j].Line_data.length; k++) {
            const item = this.f4626FormData[i].Line_data[j].Line_data[k];
            if (item.ATTRIB_CALC_FORMULA) {
              item.ATTRIB_VALUE = this.calc_final_amt(item.ATTRIB_CALC_FORMULA);
              item.IS_CHANGED_FLAG = "Y";
            }
          }
        }
      }
      if (this.f4626FormData[i].ROW_TYPE === 'DT' || this.f4626FormData[i].ROW_TYPE === 'MD') {
        for (let j = 0; j < this.f4626FormData[i].Line_data.length; j++) {
          const item = this.f4626FormData[i].Line_data[j];
          if (item.ATTRIB_CALC_FORMULA) {
            item.ATTRIB_VALUE = this.calc_final_amt(item.ATTRIB_CALC_FORMULA);
            item.IS_CHANGED_FLAG = "Y";
          }
        }
      }
    }
  }

  calc_final_amt(formula: string): number {
    try {
      formula = formula.replace(/\s+/g, '');
      
      // Check if formula has multiplication (e.g., "ATTRIB_CODE*1/2")
      const regex = /([A-Z0-9_]+)\*([0-9]+\/[0-9]+)/i;
      const match = formula.match(regex);

      if (match) {
        const extractedPart1 = match[1]; // ATTRIB_SHORT_CODE
        const extractedPart2 = match[2]; // Fraction like "1/2"
        
        for (let k = 0; k < this.f4626FormData.length; k++) {
          if (this.f4626FormData[k].ROW_TYPE === 'LH') {
            for (let j = 0; j < this.f4626FormData[k].Line_data.length; j++) {
              const rowData = this.f4626FormData[k].Line_data[j].Line_data;
              const currentTabAttribVal = rowData.filter((item: any) => 
                item.ATTRIB_SHORT_CODE === extractedPart1
              );
              
              if (currentTabAttribVal && currentTabAttribVal.length > 0) {
                const value = parseFloat(currentTabAttribVal[0].ATTRIB_VALUE) || 0;
                return value * (safeEvalFormula(extractedPart2, null, 'f4626') ?? 0);
              }
            }
          } else {
            const rowData = this.f4626FormData[k].Line_data;
            const currentTabAttribVal = rowData.filter((item: any) => 
              item.ATTRIB_SHORT_CODE === extractedPart1
            );
            
            if (currentTabAttribVal && currentTabAttribVal.length > 0) {
              const value = parseFloat(currentTabAttribVal[0].ATTRIB_VALUE) || 0;
              
              // Check if field should be zero or greater and value is negative
              if (currentTabAttribVal[0].IS_ZERO_OR_GREATER === 'Y' && value < 0) {
                return 0;
              }
              
              return value * (safeEvalFormula(extractedPart2, null, 'f4626') ?? 0);
            }
          }
        }
      } else {
        // Handle addition/subtraction formulas (e.g., "L1A+L1B+L1C+L1D")
        const lineFormulaArray = formula.split(/[+\-*/]/);
        
        for (let i = 0; i < lineFormulaArray.length; i++) {
          let lineAmt = 0;
          const lineCode = lineFormulaArray[i].trim();
          
          for (let k = 0; k < this.f4626FormData.length; k++) {
            if (this.f4626FormData[k].ROW_TYPE === 'LH') {
              for (let j = 0; j < this.f4626FormData[k].Line_data.length; j++) {
                const rowData = this.f4626FormData[k].Line_data[j].Line_data;
                const currentTabAttribVal = rowData.filter((item: any) => 
                  item.ATTRIB_SHORT_CODE === lineCode
                );
                
                if (currentTabAttribVal && currentTabAttribVal.length > 0) {
                  lineAmt += parseFloat(currentTabAttribVal[0].ATTRIB_VALUE) || 0;
                }
              }
            } else {
              const rowData = this.f4626FormData[k].Line_data;
              const currentTabAttribVal = rowData.filter((item: any) => 
                item.ATTRIB_SHORT_CODE === lineCode
              );
              
              if (currentTabAttribVal && currentTabAttribVal.length > 0) {
                lineAmt += parseFloat(currentTabAttribVal[0].ATTRIB_VALUE) || 0;
              }
            }
          }
          
          // Replace the line code with the calculated amount
          formula = formula.replace(lineCode, lineAmt.toString());
        }

        // Evaluate the final formula
        const result = safeEvalFormula(formula, null, 'f4626');
        if (result !== null && result !== undefined && !isNaN(result)) {
          console.log('Formula calculation result:', result);
          return result;
        } else {
          return 0;
        }
      }
      
      return 0;
    } catch (e) {
      console.error('Error calculating formula:', formula, e);
      return 0;
    }
  }

  checkBoxHandler(item: any, row: any, index: number): void {
    // Find all radio button siblings dynamically (same LINE_NO, EDIT_TYPE='radio', DATA_TYPE='string')
    const siblings = row.Line_data.filter((sibling: any, siblingIndex: number) => 
      siblingIndex !== index && // Not the current item
      sibling.EDIT_TYPE === 'radio' && 
      sibling.DATA_TYPE === 'string' &&
      sibling.LINE_NO === item.LINE_NO // Same line number
    );

    if (item.ATTRIB_VALUE === "Y") {
      // Current item is selected, deselect all siblings
      siblings.forEach((sibling: any) => {
        const siblingPreviousValue = sibling.ATTRIB_VALUE;
        sibling.ATTRIB_VALUE = "N";
        // If sibling was 'Y' and is being deselected, mark for deletion
        if (siblingPreviousValue === "Y") {
          sibling.IS_CHANGED_FLAG = "D";
        } else {
          sibling.IS_CHANGED_FLAG = "Y";
        }
      });
    } else {
      // Current item is deselected, select the first sibling (or handle as needed)
      // Note: For Yes/No pairs, typically only one sibling exists
      if (siblings.length > 0) {
        const firstSibling = siblings[0];
        firstSibling.ATTRIB_VALUE = "Y";
        firstSibling.IS_CHANGED_FLAG = "Y";
      }
    }
  }

  save(): void {
    this.isSaveClicked = true;
    this.crudLoading = true;

    // Check for unsaved changes
    if (_.isEqual(this.f4626FormData, this.f4626OriginalData)) {
      this.isSaveClicked = false;
      this.crudLoading = false;
      this.gtwSnackbar.openSnackBar("No changes to save.", 'gtw-snackbar--yellow', true, 4);
      return;
    }

    // Prepare final data - EXACTLY like AngularJS
    this.f4626FinalData = _.cloneDeep(this.f4626FormData);
    const finalData: any[] = [];

    // Add ALL items first (not just changed ones) - matching AngularJS logic
    for (const section of this.f4626FinalData) {
      if (section.ROW_TYPE === 'TH' || section.ROW_TYPE === 'DT' || 
          section.ROW_TYPE === 'RT' || section.ROW_TYPE === 'MD') {
        for (const item of section.Line_data) {
          if (item.ATTRIB_VALUE && item.EDIT_TYPE === 'custom_date_various' && item.DATA_TYPE === 'dateVarious') {
            item.ATTRIB_VALUE = this.getDateFormat(item.ATTRIB_VALUE);
          }
          finalData.push(item); // Push ALL items
        }
      }
      if (section.ROW_TYPE === 'LH') {
        for (const lineData of section.Line_data) {
          for (const item of lineData.Line_data) {
            if (item.ATTRIB_VALUE && item.EDIT_TYPE === 'custom_date_various' && item.DATA_TYPE === 'dateVarious') {
              item.ATTRIB_VALUE = this.getDateFormat(item.ATTRIB_VALUE);
            }
            finalData.push(item); // Push ALL items
          }
        }
      }
    }

    // Filter only changed items AFTER adding all - include both 'Y' (update/insert) and 'D' (delete)
    const changedData = finalData.filter(el => el.IS_CHANGED_FLAG === "Y" || el.IS_CHANGED_FLAG === "D");
    
    console.log("F4626 All finalData items:", finalData);
    console.log("F4626 Changed items to send:", changedData);
    console.log("F4626 Items with ATTRIB_VALUE:", finalData.filter(el => el.ATTRIB_VALUE));
    
    // Debug specific items
    changedData.forEach((item, index) => {
      console.log(`F4626 Changed item ${index}:`, {
        LINE_NO: item.LINE_NO,
        ATTRIB_SHORT_CODE: item.ATTRIB_SHORT_CODE,
        ATTRIB_VALUE: item.ATTRIB_VALUE,
        IS_CHANGED_FLAG: item.IS_CHANGED_FLAG,
        EDIT_TYPE: item.EDIT_TYPE,
        DATA_TYPE: item.DATA_TYPE,
        ATTRIB_TYPE: item.ATTRIB_TYPE,
        TRANS_DETAILS_KEY: item.TRANS_DETAILS_KEY
      });
    });
    
    // Check if we have the third row item (LINE_NO "3")
    const thirdRowItem = finalData.find(item => item.LINE_NO === "3" && item.ATTRIB_SHORT_CODE === "DATA_ATTRIB_A_3");
    if (thirdRowItem) {
      console.log("F4626 Third row item found:", {
        LINE_NO: thirdRowItem.LINE_NO,
        ATTRIB_SHORT_CODE: thirdRowItem.ATTRIB_SHORT_CODE,
        ATTRIB_VALUE: thirdRowItem.ATTRIB_VALUE,
        IS_CHANGED_FLAG: thirdRowItem.IS_CHANGED_FLAG,
        EDIT_TYPE: thirdRowItem.EDIT_TYPE,
        ATTRIB_TYPE: thirdRowItem.ATTRIB_TYPE,
        TRANS_DETAILS_KEY: thirdRowItem.TRANS_DETAILS_KEY,
        ATTRIB_CALC_FORMULA: thirdRowItem.ATTRIB_CALC_FORMULA
      });
    } else {
      console.log("F4626 Third row item NOT FOUND in finalData");
    }
    
    // Check all items with ATTRIB_TYPE = 0 (calculated fields)
    const calculatedFields = finalData.filter(item => item.ATTRIB_TYPE === 0);
    console.log("F4626 Calculated fields (ATTRIB_TYPE=0):", calculatedFields.map(item => ({
      LINE_NO: item.LINE_NO,
      ATTRIB_SHORT_CODE: item.ATTRIB_SHORT_CODE,
      ATTRIB_VALUE: item.ATTRIB_VALUE,
      ATTRIB_CALC_FORMULA: item.ATTRIB_CALC_FORMULA,
      TRANS_DETAILS_KEY: item.TRANS_DETAILS_KEY
    })));
    
    // CRITICAL: Build clobSettings like F7004 (simple structure)
    const clobSettings = {
      'sso_id': this.USER_SETTINGS?.user?.sso_id || this.globalService?.globalParams?.sso_id || '',
      'screen_key': this.rowData.SCREEN_KEY || this.globalService?.globalParams?.screen_key || '',
      'form_name': 'F4626',
      'combination_key': this.rowData.COMBINATION_KEY || ''
    };

    this.f4626Service.saveForm4626(
      this.GENERAL_CONFIG.base_url,
      changedData,
      clobSettings,
      this.currentPart,
      this.currentSchedule,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      response => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbar.openSnackBar(`Error saving data: ${response.errorMessage}`, 'gtw-snackbar--red', true, 4);
        } else {
          this.gtwSnackbar.openSnackBar('Form 4626 Details are successfully updated.', 'gtw-snackbar--green', true, 4);
          // ✅ SUCCESS: Emit STRING 'refresh' (NOT object)
          this.requestGridUpdate.emit('refresh');
        }
      },
      error => {
        console.error('Error saving F4626 data:', error);
        this.gtwSnackbar.openSnackBar('Failed to save data.', 'gtw-snackbar--red', true, 4);
        this.isSaveClicked = false;
        this.crudLoading = false;
      }
    );
  }

  reset(): void {
    this.init();
    this.gtwSnackbar.openSnackBar('Form reset.', 'gtw-snackbar--yellow', true, 2);
  }

  cancel(): void {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
  }

  getDateFormat(dateValue: any): string {
    if (!dateValue) return '';
    if (dateValue === "VARIOUS") return "";
    
    // If it's already in MM/DD/YYYY format, convert to YYYY-MM-DD for HTML5 date input
    if (typeof dateValue === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateValue)) {
      const parts = dateValue.split('/');
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';

    // Return YYYY-MM-DD format for HTML5 date input
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Radio button handler for Yes/No inputs
  onRadioChange(index: number, value: boolean, row: any): void {
    row.value = value;
    row.IS_CHANGED_FLAG = 'Y';
    // Add any field visibility logic if needed
  }

  // Checkbox toggle handler - mimics AngularJS ng-true-value/ng-false-value behavior
  toggleCheckbox(item: any, row: any, itemIndex: number, mainIndex: number): void {
    // Store previous value to determine if we're selecting or deselecting
    const previousValue = item.ATTRIB_VALUE;
    
    // Toggle between 'Y' and 'N' like AngularJS ng-true-value/ng-false-value
    item.ATTRIB_VALUE = item.ATTRIB_VALUE === 'Y' ? 'N' : 'Y';
    
    // Set IS_CHANGED_FLAG based on selection state:
    // - If deselecting (Y → N): mark for deletion ('D')
    // - If selecting (N → Y): mark for update/insert ('Y')
    if (previousValue === 'Y' && item.ATTRIB_VALUE === 'N') {
      item.IS_CHANGED_FLAG = 'D'; // Deselecting → mark for deletion
    } else {
      item.IS_CHANGED_FLAG = 'Y'; // Selecting → mark for update/insert
    }
    
    // Call the original handlers
    this.checkBoxHandler(item, row, itemIndex);
    this.valuechanged(mainIndex, null, item.LINE_NO, 0);
  }

  // Convert HTML5 date (YYYY-MM-DD) back to MM/DD/YYYY for storage
  updateDateValue(item: any, dateValue: string): void {
    if (!dateValue) {
      item.ATTRIB_VALUE = '';
      return;
    }
    
    // Convert from YYYY-MM-DD to MM/DD/YYYY
    const parts = dateValue.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parseInt(parts[1]).toString(); // Remove leading zero
      const day = parseInt(parts[2]).toString(); // Remove leading zero
      item.ATTRIB_VALUE = `${month}/${day}/${year}`;
    } else {
      item.ATTRIB_VALUE = dateValue;
    }
    item.IS_CHANGED_FLAG = 'Y';
  }
}
